/**
 * web/screens/gameIntro.ts
 * Intro-cutscene wiring extracted from {@link gameScreen} so that
 * `web/screens/game.ts` stays under the 300-line cap mandated by
 * AGENTS.md. The cutscene plays once per save (gated by
 * `hasSeenIntro`); subsequent loads return an already-closed handle
 * without ever showing the overlay.
 *
 * @module web/screens/gameIntro
 */

/// <reference lib="dom" />

import { showIntroCutscene } from '../overlays';
import * as SaveManager from '@src/game/services/saveManager';
import { getIntroLines } from '@src/game/services/narrativeManager';
import Logger from '@src/game/utils/logger';

/**
 * Handle returned by {@link startIntroCutscene}. The RAF tick gate
 * reads {@link IntroHandle.isClosed} to decide whether the engine
 * should be updating; the intro starts closed=false and flips to true
 * once the player dismisses it (or immediately if `hasSeenIntro` is
 * already persisted).
 */
export interface IntroHandle {
  /** True once the cutscene is closed (or was skipped on this load). */
  isClosed(): boolean;
}

/**
 * Mount the first-run intro cutscene over `root` (fire-and-forget).
 *
 * If the save already has `hasSeenIntro === true`, the handle reports
 * `isClosed() === true` as soon as the save read resolves and no
 * overlay is shown. Otherwise the overlay is mounted and the close
 * callback persists `hasSeenIntro` so future loads skip it.
 *
 * @param root - The game-screen root element the overlay attaches to.
 * @returns An {@link IntroHandle} the caller can poll from its RAF tick.
 */
export function startIntroCutscene(root: HTMLElement): IntroHandle {
  let closed = false;
  void (async (): Promise<void> => {
    try {
      const save = await SaveManager.load();
      if (save?.hasSeenIntro) { closed = true; return; }
    } catch {
      closed = true;
      return;
    }
    const introStrings = getIntroLines().map((l) =>
      l.speaker ? `${l.speaker}: ${l.text}` : l.text,
    );
    showIntroCutscene(root, introStrings, () => {
      closed = true;
      // Persist hasSeenIntro flag — fire-and-forget.
      void (async (): Promise<void> => {
        try {
          const save = await SaveManager.load() ?? SaveManager.createDefaultSave();
          save.hasSeenIntro = true;
          await SaveManager.save(save);
        } catch (err) {
          Logger.warn('web/gameIntro', 'Failed to persist hasSeenIntro', err);
        }
      })();
    });
  })();
  return { isClosed: () => closed };
}
